import React from 'react';

// Utility to detect and log React rendering issues
export const withRenderingDebug = (Component: React.ComponentType<any>, displayName: string) => {
  return React.forwardRef((props: any, ref: any) => {
    console.log(`[Render Debug] ${displayName} - About to render with props:`, props);
    
    try {
      const result = <Component {...props} ref={ref} />;
      
      // Check if result is a valid React element
      if (!React.isValidElement(result) && result !== null) {
        const errorMsg = `Invalid render result in ${displayName}`;
        console.error(`[Render Debug] ${errorMsg}:`, {
          result,
          type: typeof result,
          keys: Object.keys(result || {}),
          constructor: result?.constructor?.name
        });
        
        // Log to mobile debugger
        if (typeof window !== 'undefined' && (window as any).mobileDebugger) {
          (window as any).mobileDebugger.logError({
            error: `${errorMsg}: ${JSON.stringify({
              type: typeof result,
              keys: Object.keys(result || {}),
              constructor: result?.constructor?.name
            })}`,
            component: displayName
          });
        }
      }
      
      return result;
    } catch (error) {
      console.error(`[Render Debug] ${displayName} - Render error:`, error);
      console.error(`[Render Debug] ${displayName} - Props when error occurred:`, props);
      
      // Log to mobile debugger
      if (typeof window !== 'undefined' && (window as any).mobileDebugger) {
        (window as any).mobileDebugger.logError({
          error: `Render error in ${displayName}: ${error instanceof Error ? error.message : String(error)}`,
          stack: error instanceof Error ? error.stack : undefined,
          component: displayName
        });
      }
      
      throw error;
    }
  });
};

// Hook to validate children before rendering
export const useChildrenValidation = (children: React.ReactNode, componentName: string) => {
  React.useEffect(() => {
    if (children && typeof children === 'object' && !React.isValidElement(children)) {
      console.error(`[Children Debug] ${componentName} - Invalid children detected:`, {
        children,
        type: typeof children,
        keys: Object.keys(children),
        isArray: Array.isArray(children)
      });
    }
  }, [children, componentName]);
};

// Add mobile-specific debugging
export const logMobileRenderError = (componentName: string, error: any, additionalData?: any) => {
  console.error(`[Mobile Render Error] ${componentName}:`, {
    error: error.message,
    stack: error.stack,
    isMobile: window.innerWidth < 768,
    userAgent: navigator.userAgent,
    viewport: {
      width: window.innerWidth,
      height: window.innerHeight
    },
    additionalData
  });
};